import { AfterViewInit, Component, ElementRef, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { links } from '../../shared/links';
import { initFeatureFx } from '../feature-fx.util';

/**
 * Kommerzielle SEO-Landingpage „Honigautomat kaufen“ (/honigautomat).
 * Eigener Winkel: Imker-Direktvermarktung, ganzjährig lagerfähig, ein Schritt
 * vom LEH in den eigenen Vertrieb. Referenz: Honigmanufaktur mit Herz
 * (Live-Link + Video). Blog: warum-sich-ein-honigautomat-lohnt.
 */
@Component({
  selector: 'app-honigautomat-page',
  standalone: true,
  imports: [RouterModule, DecimalPipe],
  templateUrl: './honigautomat-page.component.html',
  styleUrls: ['../cluster-landing.shared.scss'],
})
export class HonigautomatPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly locale = inject(LOCALE_ID);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);
  private observers: IntersectionObserver[] = [];
  private revealFallback?: ReturnType<typeof setTimeout>;

  readonly links = links;
  readonly blogSlug = 'warum-sich-ein-honigautomat-lohnt';

  readonly liveUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    'https://live.marktbox.de/honigmanufakturmitherz/U-MB-00329'
  );

  readonly videoThumb = 'https://i.ytimg.com/vi/rEeuNltAqs8/hqdefault.jpg';
  videoOpen = false;
  videoUrl?: SafeResourceUrl;

  readonly priceBuyFrom = 6800;
  readonly priceRentFrom = 300;

  readonly faqs: { q: string; a: string }[] = [
    {
      q: $localize`:@@honig.faq1.q:Muss Honig im Automaten gekühlt werden?`,
      a: $localize`:@@honig.faq1.a:Nein. Honig ist ungekühlt lange haltbar – ideal für die marktbox als ungekühlten Automaten. Ein Standplatz am Bienenstand, Hof oder an der Straße genügt.`,
    },
    {
      q: $localize`:@@honig.faq2.q:Welche Gläser und Größen passen in den Automaten?`,
      a: $localize`:@@honig.faq2.a:Von kleinen Probiergläsern bis zum großen Glas: Die Fächer lassen sich flexibel belegen und mit Erweiterungen an dein Sortiment anpassen.`,
    },
    {
      q: $localize`:@@honig.faq3.q:Lohnt sich ein Honigautomat für Imker:innen?`,
      a: $localize`:@@honig.faq3.a:Ja – du verkaufst rund um die Uhr ohne Standdienst und machst den Schritt vom Verkauf über den Handel hin zum eigenen Vertrieb mit voller Preishoheit. Details im Ratgeber.`,
    },
    {
      q: $localize`:@@honig.faq4.q:Honigautomat kaufen oder mieten?`,
      a: $localize`:@@honig.faq4.a:Beides: ab 6.800 € kaufen oder ab 300 € pro Monat mieten – je nach Modell und Zubehör.`,
    },
  ];

  openVideo(): void {
    this.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      'https://www.youtube-nocookie.com/embed/rEeuNltAqs8?autoplay=1&rel=0'
    );
    this.videoOpen = true;
  }

  ngOnInit(): void {
    const canonical = this.canonicalUrl();
    const title = $localize`:@@honig.metaTitle:Honigautomat kaufen & mieten | marktbox`;
    const description =
      $localize`:@@honig.metaDesc:Honigautomat kaufen oder mieten: Honig 24/7 direkt ab Imkerei verkaufen – ungekühlt lagerfähig, bargeldlos. marktbox ab 6.800 € oder 300 €/Monat.`;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'keywords',
      content: $localize`:@@honig.metaKeywords:Honigautomat, Honig verkaufen Automat, Imker Direktvermarktung, Honig Automat, marktbox`,
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'marktbox' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
    });

    this.setCanonical(canonical);
    this.injectJsonLd('honigautomat-product-schema', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'marktbox Honigautomat',
      description,
      image: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
      brand: { '@type': 'Brand', name: 'marktbox' },
      category: 'Verkaufsautomat',
      offers: [
        {
          '@type': 'Offer',
          name: 'Kauf',
          priceCurrency: 'EUR',
          price: this.priceBuyFrom,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
        {
          '@type': 'Offer',
          name: 'Miete',
          priceCurrency: 'EUR',
          price: this.priceRentFrom,
          availability: 'https://schema.org/InStock',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
      ],
    });
    this.injectJsonLd('honigautomat-faq-schema', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    this.injectJsonLd('honigautomat-video-schema', {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: 'marktbox – Zu Gast in der Honigmanufaktur mit Herz',
      description:
        'Zu Besuch bei der Honigmanufaktur mit Herz: Wie Honig über die marktbox rund um die Uhr direkt vermarktet wird.',
      thumbnailUrl: 'https://i.ytimg.com/vi/rEeuNltAqs8/hqdefault.jpg',
      uploadDate: '2026-06-19',
      duration: 'PT2M30S',
      embedUrl: 'https://www.youtube-nocookie.com/embed/rEeuNltAqs8',
      contentUrl: 'https://www.youtube.com/watch?v=rEeuNltAqs8',
    });
  }

  ngAfterViewInit(): void {
    this.observers = initFeatureFx(this.host.nativeElement);
    if (typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined') {
      this.revealFallback = setTimeout(() => {
        this.host.nativeElement
          .querySelectorAll('.reveal:not(.reveal-in)')
          .forEach((el) => el.classList.add('reveal-in'));
      }, 1200);
    }
  }

  ngOnDestroy(): void {
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.revealFallback) clearTimeout(this.revealFallback);
  }

  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    this.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private canonicalUrl(): string {
    const localePrefix = this.locale.startsWith('da') ? 'da' : this.locale.startsWith('nl') ? 'nl' : 'de';
    return `https://marktbox.de/${localePrefix}/honigautomat`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel=canonical]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }

  private injectJsonLd(id: string, obj: object): void {
    this.document.getElementById(id)?.remove();
    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(obj);
    this.document.head.appendChild(script);
  }
}
